// src/pages/ResponseEvaluationPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TablePagination,
} from '@mui/material';
import { Drawer, Divider } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  getAllFormResponses,
  evaluateFormResponse,
  getFullFormResponse,
  getFormResponseForEvaluation,
} from '../controllers/formResponse.controller';

const statusOptions = [
  { label: 'همه', value: '' },
  { label: 'تأیید شده ✅', value: 'accepted' },
  { label: 'رد شده ❌', value: 'rejected' },
  { label: 'در انتظار ⏳', value: 'pending' },
];

// نام بیمار از روی subjectId (populate شده در بک‌اند)
const getSubjectName = (subject) => {
  if (!subject) return '—';
  const full =
    `${subject.firstName || ''} ${subject.lastName || ''}`.trim() ||
    subject.nationalId ||
    '';
  return full || '—';
};

const ResponseEvaluationPage = () => {
  const [responses, setResponses] = useState([]);

  // 🔹 فیلترها: وضعیت + بازه‌ی زمانی
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [evalData, setEvalData] = useState({
    status: 'pending',
    score: '',
    comment: '',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('submittedAt');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null); // هم برای دیالوگ، هم دراور
  const isMobile = window.innerWidth <= 600;

  // ---------- دریافت لیست پاسخ‌ها با فیلتر ----------
  const loadData = useCallback(async () => {
    try {
      // filters شامل status, startDate, endDate است؛
      // فرانت کنترلر باید این‌ها را به query-string بفرستد.
      const { data } = await getAllFormResponses(filters);
      setResponses(data || []);
    } catch (err) {
      console.error(err);
      toast.error('خطا در دریافت داده‌ها');
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---------- باز کردن دیالوگ ارزیابی ----------
  const openDialog = async (row) => {
    try {
      const data = await getFormResponseForEvaluation(row._id);
      setSelected(data);
      setEvalData({
        status: data.evaluation?.status || 'pending',
        score: data.evaluation?.score || '',
        comment: data.evaluation?.comment || '',
      });
      setDialogOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('❌ خطا در دریافت پاسخ برای ارزیابی');
    }
  };

  // ---------- باز کردن دراور نمایش پاسخ ----------
  const openDrawer = async (row) => {
    try {
      const { response, structure } = await getFullFormResponse(row._id);
      setSelected({ ...response, structure });
      setDrawerOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('خطا در دریافت پاسخ');
    }
  };

  // ---------- ثبت ارزیابی ----------
  const handleEvaluate = async () => {
    try {
      const result = await evaluateFormResponse(selected._id, evalData);

      if (result && result.success) {
        toast.success('✅ ارزیابی با موفقیت ثبت شد');
        setDialogOpen(false);
        loadData();
      } else {
        toast.error('⚠ ارزیابی ثبت نشد!');
      }
    } catch (error) {
      console.error('❌ خطا در ارزیابی:', error);
      toast.error('خطا در ثبت ارزیابی');
    }
  };

  // ---------- مرتب‌سازی ----------
  const sortedResponses = [...responses].sort((a, b) => {
    // برای userId و formId رشته‌ی قابل مقایسه درست می‌کنیم
    let aVal;
    let bVal;

    if (sortField === 'userId') {
      aVal = a.userId?.fullName || a.userId?.email || '';
      bVal = b.userId?.fullName || b.userId?.email || '';
    } else if (sortField === 'formId') {
      aVal = a.formId?.title?.fa || a.formId?.title?.en || '';
      bVal = b.formId?.title?.fa || b.formId?.title?.en || '';
    } else if (sortField === 'submittedAt') {
      aVal = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      bVal = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    } else {
      aVal = a[sortField] || '';
      bVal = b[sortField] || '';
    }

    return sortOrder === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  return (
    <Box sx={{ p: 3, direction: 'rtl' }}>
      <ToastContainer position="top-right" />
      <Typography variant="h5" mb={3}>
        📝 ارزیابی پاسخ‌ها
      </Typography>
      <Typography variant="subtitle1" mb={2}>
        تعداد پاسخ‌ها: {responses.length}
      </Typography>

      {/* فیلترها */}
      <Box
        sx={{
          direction: 'rtl',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        {/* فیلتر وضعیت */}
        <TextField
          select
          label="فیلتر وضعیت"
          value={filters.status}
          onChange={(e) =>
            setFilters((f) => ({ ...f, status: e.target.value }))
          }
          sx={{
            minWidth: 180,
            '& .MuiSelect-icon': { left: 8, right: 'unset' },
            '& .MuiSelect-select': { textAlign: 'right' },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  direction: 'rtl',
                  textAlign: 'right',
                  '& .MuiMenuItem-root': {
                    justifyContent: 'right',
                    textAlign: 'right',
                  },
                },
              },
            },
          }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {/* فیلتر تاریخ از */}
        <TextField
          label="از تاریخ"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filters.startDate}
          onChange={(e) =>
            setFilters((f) => ({ ...f, startDate: e.target.value }))
          }
          sx={{ minWidth: 160 }}
        />

        {/* فیلتر تاریخ تا */}
        <TextField
          label="تا تاریخ"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filters.endDate}
          onChange={(e) =>
            setFilters((f) => ({ ...f, endDate: e.target.value }))
          }
          sx={{ minWidth: 160 }}
        />
      </Box>

      {/* جدول اصلی */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>فرم</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>کاربر</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>بیمار</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>زمان ارسال</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>وضعیت</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResponses
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, i) => (
                <TableRow key={row._id || i}>
                  {/* نام فرم */}
                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSortField('formId');
                      setSortOrder((prev) =>
                        prev === 'asc' ? 'desc' : 'asc'
                      );
                    }}
                  >
                    {row.formId?.title?.fa ||
                      row.formId?.title?.en ||
                      '—'}{' '}
                    {sortField === 'formId'
                      ? sortOrder === 'asc'
                        ? '🔼'
                        : '🔽'
                      : ''}
                  </TableCell>

                  {/* کاربر */}
                  <TableCell
                    onClick={() => {
                      setSortField('userId');
                      setSortOrder((prev) =>
                        prev === 'asc' ? 'desc' : 'asc'
                      );
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    {row.userId?.fullName || row.userId?.email || 'کاربر'}{' '}
                    {sortField === 'userId'
                      ? sortOrder === 'asc'
                        ? '🔼'
                        : '🔽'
                      : ''}
                  </TableCell>

                  {/* بیمار */}
                  <TableCell>{getSubjectName(row.subjectId)}</TableCell>

                  {/* زمان ارسال */}
                  <TableCell
                    onClick={() => {
                      setSortField('submittedAt');
                      setSortOrder((prev) =>
                        prev === 'asc' ? 'desc' : 'asc'
                      );
                    }}
                    sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {row.submittedAt
                      ? new Date(row.submittedAt).toLocaleString('fa-IR')
                      : '—'}{' '}
                    {sortField === 'submittedAt'
                      ? sortOrder === 'asc'
                        ? '🔼'
                        : '🔽'
                      : ''}
                  </TableCell>

                  {/* وضعیت */}
                  <TableCell
                    sx={{
                      bgcolor:
                        {
                          accepted: '#e8f5e9',
                          rejected: '#ffebee',
                          pending: '#fffde7',
                        }[row.evaluation?.status] || '#fff',
                    }}
                  >
                    {{
                      accepted: '✅ تأیید شده',
                      rejected: '❌ رد شده',
                      pending: '⏳ در انتظار',
                    }[row.evaluation?.status] || '—'}
                  </TableCell>

                  {/* عملیات */}
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => openDialog(row)}
                      >
                        ارزیابی
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => openDrawer(row)}
                      >
                        📄 پاسخ
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={responses.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </TableContainer>

      {/* دیالوگ ارزیابی */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>ارزیابی پاسخ</DialogTitle>
        <DialogContent dir="rtl">
          <TextField
            select
            fullWidth
            label="وضعیت ارزیابی"
            value={evalData.status}
            onChange={(e) =>
              setEvalData((p) => ({ ...p, status: e.target.value }))
            }
            sx={{ mt: 2 }}
          >
            <MenuItem value="accepted">تأیید شده</MenuItem>
            <MenuItem value="rejected">رد شده</MenuItem>
            <MenuItem value="pending">در انتظار</MenuItem>
          </TextField>

          <TextField
            label="امتیاز"
            type="number"
            fullWidth
            sx={{ mt: 2 }}
            value={evalData.score}
            onChange={(e) =>
              setEvalData((p) => ({ ...p, score: e.target.value }))
            }
          />

          <TextField
            label="نظر ارزیاب"
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 2 }}
            value={evalData.comment}
            onChange={(e) =>
              setEvalData((p) => ({ ...p, comment: e.target.value }))
            }
          />

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
            📝 جزئیات پاسخ
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>انصراف</Button>
          <Button
            onClick={handleEvaluate}
            variant="contained"
            color="primary"
          >
            ثبت ارزیابی
          </Button>
        </DialogActions>
      </Dialog>

      {/* دراور نمایش پاسخ کامل */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'left'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          disableBackdropClick: true,
          keepMounted: true,
        }}
      >
        <Box sx={{ width: 500, p: 3, direction: 'rtl' }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            📄 پاسخ کاربر
          </Typography>

          {/* اگر خواستی می‌توانی اینجا هم نام بیمار را نشان بدهی */}
          {selected?.subjectId && (
            <Typography mb={2}>
              👤 بیمار: {getSubjectName(selected.subjectId)}
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1} mb={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.print()}
            >
              🖨 چاپ
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const blob = new Blob([JSON.stringify(selected, null, 2)], {
                  type: 'application/json',
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `response_${selected._id}.json`;
                link.click();
              }}
            >
              💾 دانلود JSON
            </Button>
          </Stack>

          {Array.isArray(selected?.answers) &&
          selected.answers.length > 0 &&
          selected.structure ? (
            Object.entries(
              selected.structure.reduce((acc, field) => {
                const step = field.stepNumber || 1;
                const group = field.group?.trim() || 'بدون گروه';
                if (!acc[step]) acc[step] = {};
                if (!acc[step][group]) acc[step][group] = [];
                acc[step][group].push(field);
                return acc;
              }, {})
            ).map(([step, groups]) => (
              <Box key={step} sx={{ mb: 5 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  🧭 مرحله {step}
                </Typography>
                {Object.entries(groups).map(([groupName, groupFields]) => (
                  <Box key={groupName} sx={{ mb: 4 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        py: 1,
                        px: 2,
                        borderRight: '6px solid #64b5f6',
                        borderRadius: 1,
                        mb: 2,
                        textAlign: 'right',
                      }}
                    >
                      📁 گروه: {groupName}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {groupFields.map((field, i) => {
                      const a = selected.answers.find(
                        (ans) => ans.fieldId === field.fieldId
                      );
                      const value = a?.value;

                      return (
                        <Box key={i} sx={{ mb: 3 }}>
                          <Typography
                            sx={{
                              fontSize: field.fontSize,
                              fontWeight:
                                field.style?.cssClass === 'form-bold'
                                  ? 'bold'
                                  : 'normal',
                              fontStyle:
                                field.style?.cssClass === 'form-italic'
                                  ? 'italic'
                                  : 'normal',
                              direction:
                                field.style?.cssClass === 'form-rtl'
                                  ? 'rtl'
                                  : 'ltr',
                              borderLeft: `4px solid ${
                                field.backgroundColor || '#ccc'
                              }`,
                              pl: 1,
                              mb: 1,
                            }}
                          >
                            {field.titlep || 'بدون عنوان'}
                            {field.required && (
                              <span style={{ color: 'red' }}> *</span>
                            )}
                            {field.tooltip && (
                              <span
                                title={field.tooltip}
                                style={{
                                  color: '#888',
                                  cursor: 'help',
                                  fontSize: '18px',
                                  marginRight: 6,
                                }}
                              >
                                ❔
                              </span>
                            )}
                          </Typography>

                          {typeof value === 'string' &&
                          value.startsWith('file-') ? (
                            <a
                              href={`/uploads/${value}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1976d2' }}
                            >
                              📎 دانلود فایل
                            </a>
                          ) : Array.isArray(value) ? (
                            <ul style={{ paddingRight: 20 }}>
                              {value.map((v, j) => (
                                <li key={j}>{v}</li>
                              ))}
                            </ul>
                          ) : typeof value === 'object' && value !== null ? (
                            <ul style={{ paddingRight: 20 }}>
                              {Object.entries(value).map(([k, v], j) => (
                                <li key={j}>
                                  <strong>{k}:</strong> {v}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Typography>{value ?? '—'}</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            ))
          ) : (
            <Typography color="text.secondary">
              هیچ پاسخی برای نمایش وجود ندارد.
            </Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ResponseEvaluationPage;
